
import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Clock, Zap, MessageSquare, Workflow } from 'lucide-react';

const ProductCard = ({ 
  title, 
  description, 
  icon, 
  tags, 
  detailedInfo 
}: { 
  title: string; 
  description: string; 
  icon: React.ReactNode; 
  tags: string[];
  detailedInfo: string[];
}) => {
  const { t } = useLanguage();
  
  return (
    <Card className="p-6 hover:shadow-lg transition-all duration-300 flex flex-col h-full border-gray-200 hover:border-attaboy overflow-hidden group bg-white dark:bg-gray-800 dark:border-gray-700">
      <div className="bg-attaboy-faded p-4 rounded-full inline-flex w-16 h-16 items-center justify-center mb-4 text-attaboy group-hover:bg-gradient-attaboy group-hover:text-white transition-colors duration-300 dark:bg-gray-700 dark:text-white">
        {icon}
      </div>
      <h3 className="text-xl font-bold mb-2 font-poppins dark:text-white">{title}</h3>
      <p className="text-gray-600 mb-6 flex-grow dark:text-gray-300">{description}</p>
      <div className="mb-4 flex flex-wrap gap-2">
        {tags.map((tag) => (
          <Badge key={tag} variant="outline" className="bg-gray-50 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600">
            {tag}
          </Badge>
        ))}
      </div>
      <HoverCard>
        <HoverCardTrigger asChild>
          <Button variant="outline" className="border-attaboy text-attaboy hover:bg-attaboy hover:text-white transition-all duration-300 w-full dark:border-gray-600 dark:text-gray-300 dark:hover:bg-attaboy dark:hover:text-white">
            {t('products.cta')}
          </Button>
        </HoverCardTrigger>
        <HoverCardContent className="w-96 p-4" align="center">
          <div className="space-y-2">
            <h4 className="text-lg font-semibold">{title}</h4>
            {detailedInfo.map((paragraph, index) => (
              <p key={index} className="text-sm text-gray-600 dark:text-gray-300">
                {paragraph}
              </p>
            ))}
          </div>
        </HoverCardContent>
      </HoverCard>
    </Card>
  );
};

const ProductsSection = () => {
  const { t, language } = useLanguage();

  // Detailed information for each product
  const productDetails = {
    dailyTools: {
      en: [
        "Our Daily Life Tools suite offers a comprehensive set of applications designed to streamline your everyday tasks and boost your productivity.",
        "From smart calendars that learn your scheduling preferences to automated to-do lists that prioritize tasks based on your habits and deadlines, our tools adapt to your unique workflow.",
        "The suite includes time tracking features that provide insights into how you spend your day, helping you identify opportunities to reclaim valuable time and focus on what matters most.",
        "With seamless integration across devices and platforms, your productivity tools are always accessible whether you're at your desk or on the go."
      ],
      fr: [
        "Notre suite d'outils quotidiens offre un ensemble complet d'applications conçues pour simplifier vos tâches quotidiennes et augmenter votre productivité.",
        "Des calendriers intelligents qui apprennent vos préférences de planification aux listes de tâches automatisées qui hiérarchisent les tâches en fonction de vos habitudes et de vos délais, nos outils s'adaptent à votre flux de travail unique.",
        "La suite comprend des fonctionnalités de suivi du temps qui vous donnent un aperçu de la façon dont vous passez votre journée, vous aidant à identifier les opportunités de récupérer un temps précieux et de vous concentrer sur ce qui compte le plus.",
        "Avec une intégration transparente sur tous les appareils et plateformes, vos outils de productivité sont toujours accessibles, que vous soyez à votre bureau ou en déplacement."
      ]
    },
    automation: {
      en: [
        "Our Workflow Automation platform transforms manual, repetitive business processes into streamlined automated workflows that save time and reduce errors.",
        "The intuitive drag-and-drop interface allows non-technical users to design complex automation sequences without writing a single line of code, democratizing access to powerful efficiency tools.",
        "Advanced features include conditional logic, approval workflows, and integration with hundreds of popular business applications and services for a truly connected experience.",
        "Real-time monitoring and analytics provide visibility into your automated processes, helping you identify bottlenecks and continuously improve your operational efficiency."
      ],
      fr: [
        "Notre plateforme d'automatisation des flux de travail transforme les processus d'entreprise manuels et répétitifs en flux de travail automatisés rationalisés qui font gagner du temps et réduisent les erreurs.",
        "L'interface intuitive de glisser-déposer permet aux utilisateurs non techniques de concevoir des séquences d'automatisation complexes sans écrire une seule ligne de code, démocratisant l'accès à de puissants outils d'efficacité.",
        "Les fonctionnalités avancées comprennent la logique conditionnelle, les flux de travail d'approbation et l'intégration avec des centaines d'applications et de services commerciaux populaires pour une expérience véritablement connectée.",
        "La surveillance et l'analyse en temps réel offrent une visibilité sur vos processus automatisés, vous aidant à identifier les goulots d'étranglement et à améliorer continuellement votre efficacité opérationnelle."
      ]
    },
    assistant: {
      en: [
        "Meet your personal AI assistant, designed to be your digital companion throughout the workday and beyond, handling tasks and providing information with unprecedented accuracy and contextual awareness.",
        "Using advanced natural language processing, our assistant understands the nuances of human communication, allowing for natural conversations and reducing the friction of human-computer interaction.",
        "The assistant continuously learns from your preferences and behaviors, becoming increasingly personalized and proactive in anticipating your needs and providing relevant suggestions.",
        "Built with privacy by design, all interactions are processed with industry-leading security measures, ensuring your sensitive information remains confidential and protected."
      ],
      fr: [
        "Rencontrez votre assistant personnel IA, conçu pour être votre compagnon numérique tout au long de la journée de travail et au-delà, gérant les tâches et fournissant des informations avec une précision et une conscience contextuelle sans précédent.",
        "En utilisant un traitement avancé du langage naturel, notre assistant comprend les nuances de la communication humaine, permettant des conversations naturelles et réduisant les frictions de l'interaction homme-machine.",
        "L'assistant apprend continuellement de vos préférences et comportements, devenant de plus en plus personnalisé et proactif pour anticiper vos besoins et fournir des suggestions pertinentes.",
        "Construit avec la confidentialité dès la conception, toutes les interactions sont traitées avec des mesures de sécurité de pointe, garantissant que vos informations sensibles restent confidentielles et protégées."
      ]
    },
    integration: {
      en: [
        "Our API Integration platform provides a unified gateway to connect your existing systems with our AI-powered services, enabling seamless data flow and functionality across your technology stack.",
        "With support for RESTful, GraphQL, and webhook interfaces, developers can choose the integration method that best suits their application architecture and technical requirements.",
        "Comprehensive documentation, including interactive API references, code samples in multiple programming languages, and step-by-step tutorials make implementation straightforward for developers of all skill levels.",
        "Enterprise-grade features such as rate limiting, authentication options, and robust error handling ensure reliable and secure integrations even for mission-critical applications."
      ],
      fr: [
        "Notre plateforme d'intégration API fournit une passerelle unifiée pour connecter vos systèmes existants à nos services basés sur l'IA, permettant un flux de données et des fonctionnalités transparents dans votre pile technologique.",
        "Avec la prise en charge des interfaces RESTful, GraphQL et webhook, les développeurs peuvent choisir la méthode d'intégration qui convient le mieux à leur architecture d'application et à leurs exigences techniques.",
        "Une documentation complète, comprenant des références API interactives, des exemples de code dans plusieurs langages de programmation et des tutoriels étape par étape, rendent la mise en œuvre simple pour les développeurs de tous niveaux de compétence.",
        "Des fonctionnalités de niveau entreprise telles que la limitation du débit, les options d'authentification et la gestion robuste des erreurs garantissent des intégrations fiables et sécurisées, même pour les applications critiques."
      ]
    }
  };

  return (
    <section className="py-24 pb-36 relative" id="products">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-800">
        <div className="absolute inset-0 opacity-5" style={{ 
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'20\' height=\'20\' viewBox=\'0 0 20 20\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23000000\' fill-opacity=\'0.4\' fill-rule=\'evenodd\'%3E%3Ccircle cx=\'3\' cy=\'3\' r=\'3\'/%3E%3Ccircle cx=\'13\' cy=\'13\' r=\'3\'/%3E%3C/g%3E%3C/svg%3E")',
        }}></div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 font-poppins dark:text-white">{t('products.title')}</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto dark:text-gray-300">{t('products.subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Daily Tools Product */}
          <ProductCard
            title={t('product.dailytools.title')}
            description={t('product.dailytools.desc')}
            icon={<Clock className="h-8 w-8" />}
            tags={['Productivity', 'Daily Use', 'Tools']}
            detailedInfo={language === 'en' ? productDetails.dailyTools.en : productDetails.dailyTools.fr}
          />

          {/* Automation Product */}
          <ProductCard
            title={t('product.automation.title')}
            description={t('product.automation.desc')}
            icon={<Zap className="h-8 w-8" />}
            tags={['Automation', 'Workflow', 'No-Code']}
            detailedInfo={language === 'en' ? productDetails.automation.en : productDetails.automation.fr}
          />

          {/* AI Assistant Product */}
          <ProductCard
            title={t('product.assistant.title')}
            description={t('product.assistant.desc')}
            icon={<MessageSquare className="h-8 w-8" />}
            tags={['AI', 'Assistant', 'Productivity']}
            detailedInfo={language === 'en' ? productDetails.assistant.en : productDetails.assistant.fr}
          />

          {/* API Integration Product */}
          <ProductCard
            title={t('product.integration.title')}
            description={t('product.integration.desc')}
            icon={<Workflow className="h-8 w-8" />}
            tags={['API', 'Integration', 'Developers']}
            detailedInfo={language === 'en' ? productDetails.integration.en : productDetails.integration.fr}
          />
        </div>
      </div>
    </section>
  );
};

export default ProductsSection;
